import { DefaultTFuncReturn } from 'i18next';
import React from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { ScheduleListItem } from '../../../api/model';
import NoDataComponent from '../../atoms/NoDataComponent';
import WaitComponent from '../../atoms/WaitComponent';
import PageNavi from '../../modules/PageNavi';
import SlideToggle from '../../modules/SlideToggle';
import ScheduleListIndexChildRow from './ScheduleListIndexChildRow';
import { N_DATA_PER_PAGE, ScheduleSortKey, getFormattedDate, getFormattedYYMMDDFromStr } from './SchedulelistCommon';

interface Props {
  openGanttChart: boolean;
  data: ScheduleListItem[];
  allDatalength: number;
  isLoading: boolean;
  isError: boolean;
  isShowChild: (targetProductionId: string) => boolean;
  changeSortKey: (key: ScheduleSortKey[]) => void;
  changeCurrentPage: (next: number) => void;
  changeChildList: (targetPsVersionId: string) => void;
  currentPage: number;
}

/**
 * 詳細表示欄
 * @param param0
 * @returns
 */
function ScheduleListIndexArea({
  openGanttChart,
  data,
  allDatalength,
  isLoading,
  isError,
  isShowChild,
  changeSortKey,
  changeCurrentPage,
  changeChildList,
  currentPage,
}: Props) {
  const {
    t,
    i18n: { language },
  } = useTranslation();

  // データ取得中
  if (isLoading) {
    return <WaitComponent />;
  }

  // エラー表示
  if (isError) {
    return null;
  }

  // データが空の場合
  if (data == null || data == null) {
    return <NoDataComponent />;
  }

  return (
    <SlideToggle open={openGanttChart}>
      <div className="wfr documentlist in-pgnavi">
        <div className="scroll">
          <table className="table24">
            <tbody>
              <tr key="headder">
                <SortableTh label={t('管理No.')} onClick={() => changeSortKey(['production_no'])} />
                <SortableTh label={t('種別')} onClick={() => changeSortKey(['production_division_id'])} />
                <SortableTh label={t('言語')} onClick={() => changeSortKey(['language_id'])} />
                <SortableTh label={t('原作メーカー')} onClick={() => changeSortKey(['maker_company_id'])} />
                <SortableTh label={t('制作前(予定)')} onClick={() => changeSortKey(['preproduction', 'plan'])} />
                <SortableTh label={t('制作前(実績)')} onClick={() => changeSortKey(['preproduction', 'actual'])} />
                <SortableTh label={t('G1(予定)')} onClick={() => changeSortKey(['g1', 'plan'])} />
                <SortableTh label={t('G1(実績)')} onClick={() => changeSortKey(['g1', 'actual'])} />
                <SortableTh label={t('G2(予定)')} onClick={() => changeSortKey(['g2', 'plan'])} />
                <SortableTh label={t('G2(実績)')} onClick={() => changeSortKey(['g2', 'actual'])} />
                <SortableTh label={t('G3(予定)')} onClick={() => changeSortKey(['g3', 'plan'])} />
                <SortableTh label={t('G3(実績)')} onClick={() => changeSortKey(['g3', 'actual'])} />
                <SortableTh label={t('G4(予定)')} onClick={() => changeSortKey(['g4', 'plan'])} />
                <SortableTh label={t('G4(実績)')} onClick={() => changeSortKey(['g4', 'actual'])} />
                <SortableTh label={t('完成日(予定)')} onClick={() => changeSortKey(['completion', 'plan'])} />
                <SortableTh label={t('完成日(実績)')} onClick={() => changeSortKey(['completion', 'actual'])} />
                <SortableTh label={t('量産日')} onClick={() => changeSortKey(['mass_production'])} />
                <SortableTh
                  label={t('ドラフト翻訳日(予定)')}
                  onClick={() => changeSortKey(['draft_translation', 'plan'])}
                />
                <SortableTh
                  label={t('ドラフト翻訳日(実績)')}
                  onClick={() => changeSortKey(['draft_translation', 'actual'])}
                />
                <SortableTh
                  label={t('最終翻訳日(予定)')}
                  onClick={() => changeSortKey(['final_translation', 'plan'])}
                />
                <SortableTh
                  label={t('最終翻訳日(実績)')}
                  onClick={() => changeSortKey(['final_translation', 'actual'])}
                />
                <SortableTh
                  label={t('他言語編集依頼日(予定)')}
                  onClick={() => changeSortKey(['other_language_edit_request', 'plan'])}
                />
                <SortableTh
                  label={t('他言語編集依頼日(実績)')}
                  onClick={() => changeSortKey(['other_language_edit_request', 'actual'])}
                />
                <SortableTh label={t('データトス日(予定)')} onClick={() => changeSortKey(['data_toss', 'plan'])} />
                <SortableTh label={t('データトス日(実績)')} onClick={() => changeSortKey(['data_toss', 'actual'])} />
              </tr>
              {/* データ表示部 */}
              {data.map((chart, i) => (
                <React.Fragment key={chart.production_id}>
                  <tr
                    key={`test${i.toString()}`}
                    data-key-str={`${chart.production_id}}`}
                    onClick={() => {
                      changeChildList(chart.production_no);
                    }}
                  >
                    {/* 資料管理No クリック時に制作一覧/日程タブへ遷移 */}
                    <td>
                      <Link
                        to={`/${language}/productions/edit/${chart.production_id?.toString() ?? ''}#schedule`}
                        target="_blank"
                      >
                        {chart.production_no}
                      </Link>
                    </td>
                    {/* 資料区分名 */}
                    <td>{chart.production_division_name}</td>
                    {/* 言語名 */}
                    <td>{chart.language_name}</td>
                    {/* 原作メーカー名 */}
                    <td>{chart.maker_company_name}</td>
                    {/* 制作前（予定） */}
                    <td>{periodStr(chart.preproduction.plan_from_on, chart.preproduction.plan_end_on)}</td>
                    {/* 制作前（実績） */}
                    <td>{periodStr(chart.preproduction.actual_from_on, chart.preproduction.actual_end_on)}</td>
                    {/* G1（予定） */}
                    <td>{periodStr(chart.g1.plan_from_on, chart.g1.plan_end_on)}</td>
                    {/* G1（実績） */}
                    <td>{periodStr(chart.g1.actual_from_on, chart.g1.actual_end_on)}</td>
                    {/* G2（予定） */}
                    <td>{periodStr(chart.g2.plan_from_on, chart.g2.plan_end_on)}</td>
                    {/* G2（実績） */}
                    <td>{periodStr(chart.g2.actual_from_on, chart.g2.actual_end_on)}</td>
                    {/* G3（予定） */}
                    <td>{periodStr(chart.g3.plan_from_on, chart.g3.plan_end_on)}</td>
                    {/* G3（実績） */}
                    <td>{periodStr(chart.g3.actual_from_on, chart.g3.actual_end_on)}</td>
                    {/* G4（予定） */}
                    <td>{periodStr(chart.g4.plan_from_on, chart.g4.plan_end_on)}</td>
                    {/* G4（実績） */}
                    <td>{periodStr(chart.g4.actual_from_on, chart.g4.actual_end_on)}</td>
                    {/* 完成日（予定） */}
                    <td>{periodStr(chart.completion.plan_from_on, chart.completion.plan_end_on)}</td>
                    {/* 完成日（実績） */}
                    <td>{periodStr(chart.completion.actual_from_on, chart.completion.actual_end_on)}</td>
                    {/* 量産日 */}
                    <td>{getFormattedYYMMDDFromStr(chart.mass_production)}</td>
                    {/* ドラフト翻訳日（予定） */}
                    <td>{periodStr(chart.draft_translation.plan_from_on, chart.draft_translation.plan_end_on)}</td>
                    {/* ドラフト翻訳日（実績） */}
                    <td>{periodStr(chart.draft_translation.actual_from_on, chart.draft_translation.actual_end_on)}</td>
                    {/* 最終翻訳日（予定） */}
                    <td>{periodStr(chart.final_translation?.plan_from_on, chart.final_translation?.plan_end_on)}</td>
                    {/* 最終翻訳日（実績） */}
                    <td>
                      {periodStr(chart.final_translation?.actual_from_on, chart.final_translation?.actual_end_on)}
                    </td>
                    {/* 他言語編集依頼日（予定） */}
                    <td>
                      {periodStr(
                        chart.other_language_edit_request?.plan_from_on,
                        chart.other_language_edit_request?.plan_end_on
                      )}
                    </td>
                    {/* 他言語編集依頼日（実績） */}
                    <td>
                      {periodStr(
                        chart.other_language_edit_request?.actual_from_on,
                        chart.other_language_edit_request?.actual_end_on
                      )}
                    </td>
                    {/* データトス日（予定） */}
                    <td>{periodStr(chart.data_toss.plan_from_on, chart.data_toss.plan_end_on)}</td>
                    {/* データトス日（実績） */}
                    <td>{periodStr(chart.data_toss.actual_from_on, chart.data_toss.actual_end_on)}</td>
                  </tr>
                  {/* if 子日程があれば追加 class名付与必要 */}
                  {chart.children !== undefined && (
                    <ScheduleListIndexChildRow chart={chart} isShowChild={isShowChild} />
                  )}
                </React.Fragment>
              ))}
            </tbody>
          </table>
        </div>
        {/* ページング */}
        <PageNavi
          current={currentPage}
          pageCount={Math.ceil(allDatalength / (N_DATA_PER_PAGE * 1.0))}
          onChangePage={changeCurrentPage}
        />
      </div>
    </SlideToggle>
  );
}

/**
 * 期間文字列生成
 * (fromDate,toDate) => "YY/MM/DD~YY/MM/DD"に変換
 * @param start 対象期間開始日
 * @param end 対象期間終了日
 * @returns
 */
export function periodStr(start: string | undefined, end: string | undefined) {
  // どちらも値が入っていない場合
  if (start === undefined && end === undefined) {
    return '';
  }

  // 対象期間終了日にのみ値が入っていた場合
  if (start === undefined || start === null) {
    if (end !== undefined && end !== null) {
      const endDay = new Date(end);
      return `~${getFormattedDate(endDay, 'yy/MM/dd')}`;
    }
    return '';
  }

  // 対象期間開始日にのみ値が入っていた場合
  if (end === undefined || end === null) {
    if (start !== undefined && start !== null) {
      const startDay = new Date(start);
      return `${getFormattedDate(startDay, 'yy/MM/dd')}~`;
    }
    return '';
  }

  // どちらにも値が入っていた場合
  const startDay = new Date(start);
  const endDay = new Date(end);
  const startStr = getFormattedDate(startDay, 'yy/MM/dd');
  const endStr = getFormattedDate(endDay, 'yy/MM/dd');
  return `${startStr}~${endStr}`;
}

interface SortableThProps {
  label: DefaultTFuncReturn;
  onClick: () => void;
}

/**
 * ソート可能なテーブルヘッダー
 * @param param0
 * @returns
 */
export function SortableTh({ label, onClick }: SortableThProps) {
  return (
    <th>
      <button type="button" className="text-button" onClick={onClick}>
        {label}
        <i className="fa-solid fa-sort" />
      </button>
    </th>
  );
}

export default ScheduleListIndexArea;
